// src/pages/AIZone.ts
import "./AIZone.css";
import { renderAIZoneSidebar } from "../components/AIZoneSidebar";
import { renderAIZoneSearchBar } from "../components/AIZoneSearchBar";
import {
  renderCodeBlocks,
  type AICodeBlock,
} from "../components/Code";

interface AISource {
  title?: string;
  url?: string;
  domain?: string;
  snippet?: string;
}

interface Message {
  role: "user" | "ai";
  text: string;
  codeBlocks?: AICodeBlock[];
  sources?: AISource[];
  timestamp?: Date;
}

interface AIZoneApiResponse {
  degraded?: boolean;
  cooldown?: boolean;
  answer?: string;
  error?: string;
  conversation_id?: string;
  rewritten_query?: string;
  grounded?: boolean;
  memory_answer?: boolean;
  history_turns?: number;
  sources?: AISource[];
  code_blocks?: AICodeBlock[];
}

const AI_ZONE_API_URL = "https://api.ayesearch.com/api/ai/zone/";
const AI_ZONE_CONVERSATION_KEY = "ayesearch_ai_zone_conversation_id";

const AI_ZONE_APPS_LIST = [
  {
    name: "VibeJoos",
    icon: "▶",
    color: "#ff6b6b",
    image: "",
  },
  {
    name: "AMailMe",
    icon: "",
    color: "transparent",
    image: "/static/ayesearch/Amailme_Icon.png",
  },
  {
    name: "OreChat",
    icon: "",
    color: "transparent",
    image: "/static/ayesearch/OreChat_Icon.png",
  },
  {
    name: "ADrive",
    icon: "☁",
    color: "#ffd43b",
    image: "",
  },
  {
    name: "ADocs",
    icon: "📄",
    color: "#845ef7",
    image: "",
  },
  {
    name: "ANote",
    icon: "📝",
    color: "#f06595",
    image: "",
  },
];

let messages: Message[] = [];

function escapeHtml(unsafe: string): string {
  return unsafe
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function generateAIZoneAppsMenuHtml(): string {
  return `
    <div class="aiz-apps-menu hidden" id="apps-menu">
      <div class="aiz-apps-menu-grid">
        ${AI_ZONE_APPS_LIST.map(
          (app) => `
            <div class="aiz-app-item">
              ${
                app.image
                  ? `<img
                      class="aiz-app-icon aiz-app-icon-image"
                      src="${app.image}"
                      alt="${escapeHtml(app.name)}"
                    />`
                  : `<div
                      class="aiz-app-icon"
                      style="background-color: ${app.color}"
                      aria-hidden="true"
                    >${app.icon}</div>`
              }
              <span>${escapeHtml(app.name)}</span>
            </div>
          `,
        ).join("")}
      </div>

      <div class="aiz-apps-download-hub">
        <div class="aiz-apps-download-hub-header">
          <img
            class="aiz-apps-browser-icon"
            src="/static/ayesearch/Ayesearch_Icon.png"
            alt="AyeSearch"
          />
          <div class="aiz-apps-browser-copy">
            <strong>Get AyeSearch</strong>
            <span>Choose mobile or desktop browser.</span>
          </div>
        </div>

        <div class="aiz-apps-download-platforms">
          <div class="aiz-apps-download-platform">
            <span class="aiz-apps-download-kicker">Mobile app</span>
            <strong>iOS / Android</strong>
            <a
              class="aiz-apps-browser-download-btn"
              href="https://ayesearch.com/downloads/update"
              aria-label="Download AyeSearch for iOS or Android"
            >
              Download
            </a>
          </div>

          <div class="aiz-apps-download-platform">
            <span class="aiz-apps-download-kicker">AyeSearch Browser</span>
            <strong>Mac / Windows</strong>
            <a
              class="aiz-apps-browser-download-btn"
              href="https://ayesearch.com/downloads/update"
              aria-label="Download AyeSearch Browser for Mac or Windows"
            >
              Download
            </a>
          </div>
        </div>
      </div>
    </div>
  `;
}

export function renderAIZone() {
  const app = document.getElementById("app");
  if (!app) return;

  app.innerHTML = `
    <div class="ai-wrapper">
      <div id="ai-sidebar-container"></div>

      <div class="ai-main">
        <div class="ai-topnav">
          <div class="nav-left">
            <button class="hamburger" id="hamburger-btn">☰</button>
            <button class="nav-item active">AI Zone</button>
            <button class="nav-item">All</button>
            <button class="nav-item">Images</button>
          </div>

          <div class="nav-right">
            <div class="aiz-apps-dropdown-container">
              <button
                class="aiz-apps-btn"
                id="apps-btn"
                type="button"
                aria-label="Open apps"
                aria-expanded="false"
              >
                <svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true">
                  <circle cx="5" cy="5" r="2.5"/>
                  <circle cx="12" cy="5" r="2.5"/>
                  <circle cx="19" cy="5" r="2.5"/>
                  <circle cx="5" cy="12" r="2.5"/>
                  <circle cx="12" cy="12" r="2.5"/>
                  <circle cx="19" cy="12" r="2.5"/>
                  <circle cx="5" cy="19" r="2.5"/>
                  <circle cx="12" cy="19" r="2.5"/>
                  <circle cx="19" cy="19" r="2.5"/>
                </svg>
              </button>

              ${generateAIZoneAppsMenuHtml()}
            </div>

            <button class="signin-btn" id="signin-btn">Sign In</button>
          </div>
        </div>

        <div class="ai-chat-feed" id="chat-feed"></div>

        <div class="ai-chat-input">
          <div id="ai-searchbar-container"></div>
        </div>
      </div>
    </div>
  `;

  setupAIZoneEvents();
}

function setupAIZoneEvents() {
  const sidebarContainer = document.getElementById("ai-sidebar-container");
  if (sidebarContainer) renderAIZoneSidebar(sidebarContainer);

  const searchContainer = document.getElementById("ai-searchbar-container");
  if (searchContainer) {
    renderAIZoneSearchBar(searchContainer, handleSend);
  }

  const hamburgerBtn = document.getElementById("hamburger-btn");

  hamburgerBtn?.addEventListener("click", (event) => {
    event.stopPropagation();
    document.getElementById("ai-sidebar")?.classList.toggle("open");
  });

  const appsBtn = document.getElementById("apps-btn");
  const appsMenu = document.getElementById("apps-menu");

  appsBtn?.addEventListener("click", (event) => {
    event.stopPropagation();

    if (!appsMenu) return;

    const isHidden = appsMenu.classList.contains("hidden");
    appsMenu.classList.toggle("hidden", !isHidden);
    appsBtn.setAttribute("aria-expanded", String(isHidden));
  });

  document.addEventListener("click", (event) => {
    const target = event.target;
    if (!(target instanceof Node)) return;

    // Close AI Zone sidebar when clicking outside it.
    const sidebar = document.getElementById("ai-sidebar");
    if (
      sidebar?.classList.contains("open") &&
      !sidebar.contains(target) &&
      !hamburgerBtn?.contains(target)
    ) {
      sidebar.classList.remove("open");

      const sidebarToggle = document.getElementById("sidebar-toggle");
      if (sidebarToggle) sidebarToggle.textContent = "☰";
    }

    // Close apps dropdown when clicking outside it.
    if (
      appsMenu &&
      !appsMenu.contains(target) &&
      !appsBtn?.contains(target)
    ) {
      appsMenu.classList.add("hidden");
      appsBtn?.setAttribute("aria-expanded", "false");
    }
  });

  document.getElementById("signin-btn")?.addEventListener("click", () => {
    const next = encodeURIComponent(window.location.href);
    window.location.assign(
      `https://api.ayesearch.com/auth/ayesearch/login/?next=${next}`,
    );
  });

  renderMessages();
}

function renderMessages() {
  const feed = document.getElementById("chat-feed");
  if (!feed) return;

  feed.innerHTML = messages
    .map(
      (msg, index) => `
      <div class="ai-msg ${msg.role}">
        <div class="msg-content">${escapeHtml(msg.text)}</div>
        ${
          msg.role === "ai" && msg.codeBlocks?.length
            ? `<div class="msg-code-blocks" id="msg-code-${index}"></div>`
            : ""
        }
        ${
          msg.role === "ai" && msg.sources?.length
            ? `
              <div class="aiz-sources">
                <div class="aiz-sources-title">Sources</div>
                ${msg.sources
                  .filter((source) => source.url)
                  .map(
                    (source) => `
                      <a
                        class="aiz-source-card"
                        href="${escapeHtml(source.url || "")}"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <span class="aiz-source-title">${escapeHtml(
                          source.title || source.domain || source.url || "Source",
                        )}</span>
                        ${
                          source.domain
                            ? `<span class="aiz-source-domain">${escapeHtml(
                                source.domain,
                              )}</span>`
                            : ""
                        }
                      </a>
                    `,
                  )
                  .join("")}
              </div>
            `
            : ""
        }
      </div>
    `,
    )
    .join("");

  messages.forEach((msg, index) => {
    if (msg.role !== "ai" || !msg.codeBlocks?.length) return;

    const codeContainer = document.getElementById(`msg-code-${index}`);
    if (codeContainer) {
      renderCodeBlocks(codeContainer, msg.codeBlocks);
    }
  });

  feed.scrollTop = feed.scrollHeight;
}

function getConversationId(): string {
  try {
    return window.localStorage.getItem(AI_ZONE_CONVERSATION_KEY) || "";
  } catch {
    return "";
  }
}

function saveConversationId(conversationId: string): void {
  if (!conversationId) return;

  try {
    window.localStorage.setItem(
      AI_ZONE_CONVERSATION_KEY,
      conversationId,
    );
  } catch {
    // Keep current request functional if storage is unavailable.
  }
}

async function handleSend(query: string) {
  if (!query.trim()) return;

  messages.push({ role: "user", text: query });
  renderMessages();

  const loadingId = `loading-${Date.now()}`;
  const feed = document.getElementById("chat-feed");
  if (!feed) return;

  const loadingHTML = `
    <div class="ai-msg ai" id="${loadingId}">
      <div class="typing-indicator">Thinking<span class="dots"></span></div>
    </div>`;

  feed.insertAdjacentHTML("beforeend", loadingHTML);
  feed.scrollTop = feed.scrollHeight;

  try {
    const conversationId = getConversationId();

    const res = await fetch(AI_ZONE_API_URL, {
      method: "POST",
      credentials: "omit",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({
        q: query,
        conversation_id: conversationId || undefined,
      }),
    });

    const data = (await res.json()) as AIZoneApiResponse;

    if (res.status === 429 && data.cooldown) {
      document.getElementById(loadingId)?.remove();

      messages.push({
        role: "ai",
        text:
          data.answer ||
          "AI Zone is on cool down, we reached max searches, try again later",
        sources: [],
      });

      renderMessages();
      return;
    }

    if (!res.ok) {
      document.getElementById(loadingId)?.remove();

      messages.push({
        role: "ai",
        text:
          data.answer ||
          (res.status === 503
            ? "AI Zone is temporarily unavailable. Please try again."
            : data.error || `AI Zone returned HTTP ${res.status}.`),
        sources: [],
      });

      renderMessages();
      return;
    }

    if (data.conversation_id) {
      saveConversationId(data.conversation_id);
    }

    const text =
      typeof data.answer === "string" && data.answer.trim()
        ? data.answer
        : data.code_blocks?.length
          ? ""
          : "Sorry, I couldn't generate a response.";

    document.getElementById(loadingId)?.remove();

    messages.push({
      role: "ai",
      text,
      codeBlocks: Array.isArray(data.code_blocks)
        ? data.code_blocks
        : [],
      // Backend guarantees these are only Groq-returned search result URLs.
      sources: Array.isArray(data.sources) ? data.sources : [],
    });

    renderMessages();
  } catch (err) {
    console.error("AI Zone request failed:", err);

    document.getElementById(loadingId)?.remove();

    messages.push({
      role: "ai",
      text: "Error connecting to AI. Please try again later.",
    });

    renderMessages();
  }
}
